import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Loader2, Car, Truck, Bus, Bike, Package } from "lucide-react";
import { validatePlate, formatPlate } from "../utils/plateValidator";
import { useIsMobile } from "../hooks/useIsMobile";

const TYPES = [
  { value:"Car",         icon:Car,     label:"Car"     },
  { value:"Truck",       icon:Truck,   label:"Truck"   },
  { value:"Bus",         icon:Bus,     label:"Bus"     },
  { value:"Two-Wheeler", icon:Bike,    label:"2-Wheeler"},
  { value:"Other",       icon:Package, label:"Other"   },
];

export default function AddVehicleModal({ onClose, onSubmit, loading }) {
  const [plate,   setPlate]   = useState("");
  const [type,    setType]    = useState("Car");
  const [error,   setError]   = useState("");
  const [focused, setFocused] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validatePlate(plate)) {
      setError("Enter a valid plate number — e.g. MH12AB1234");
      return;
    }
    const ok = await onSubmit({ plateNumber: formatPlate(plate), vehicleType: type });
    if (ok) onClose();
  }

  return createPortal(
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      transition={{ duration:0.15 }}
      style={{
        position:"fixed", inset:0, zIndex:99999,
        background:"rgba(15,23,42,0.45)",
        display:"flex",
        alignItems: isMobile ? "flex-end" : "center",
        justifyContent:"center",
        padding: isMobile ? 0 : 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: isMobile ? 1 : 0.97, y: isMobile ? 60 : 14, opacity:0 }}
        animate={{ scale:1, y:0, opacity:1 }}
        exit={{   scale: isMobile ? 1 : 0.97, y: isMobile ? 60 : 14, opacity:0 }}
        transition={{ duration:0.22, ease:"easeOut" }}
        style={{
          width:"100%",
          maxWidth: isMobile ? "100%" : 400,
          background:"#ffffff",
          borderRadius: isMobile ? "20px 20px 0 0" : 16,
          overflow:"hidden",
          boxShadow: isMobile
            ? "0 -8px 40px rgba(0,0,0,0.18)"
            : "0 32px 80px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06)",
          border: isMobile ? "none" : "1px solid #EBEBEB",
        }}
      >
        {/* Drag handle on mobile */}
        {isMobile && (
          <div style={{ padding:"12px 0 4px", display:"flex", justifyContent:"center" }}>
            <div style={{ width:40, height:4, borderRadius:99, background:"#E2E8F0" }} />
          </div>
        )}

        {/* Header */}
        <div style={{
          padding: isMobile ? "12px 20px 14px" : "20px 22px 16px",
          borderBottom:"1px solid #F3F4F6",
          display:"flex", alignItems:"center", justifyContent:"space-between",
        }}>
          <div>
            <div style={{ fontSize:17, fontWeight:700, color:"#111827", letterSpacing:-0.2 }}>Add Vehicle</div>
            <div style={{ fontSize:12, color:"#9CA3AF", marginTop:2 }}>
              Enter plate number to fetch FASTag data
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background:"none", border:"1.5px solid #EBEBEB", borderRadius:8,
              width:32, height:32, cursor:"pointer", color:"#9CA3AF",
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.15s",
            }}
            onMouseOver={e => { e.currentTarget.style.background="#F5F5F5"; e.currentTarget.style.color="#374151"; }}
            onMouseOut={e  => { e.currentTarget.style.background="none";    e.currentTarget.style.color="#9CA3AF"; }}
          ><X size={14}/></button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: isMobile ? "18px 20px 24px" : "22px" }}>
          {/* Plate number */}
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:7, letterSpacing:0.1 }}>
              Number Plate <span style={{ color:"#EF4444" }}>*</span>
            </label>
            <input
              value={plate}
              onChange={e => { setPlate(e.target.value.toUpperCase()); setError(""); }}
              placeholder="MH12AB1234"
              maxLength={13}
              autoFocus={!isMobile}
              style={{
                width:"100%", boxSizing:"border-box",
                height:52, padding:"0 14px",
                background: focused ? "#fff" : "#F7F7F7",
                border:`2px solid ${error ? "#FCA5A5" : focused ? "#4F46E5" : "transparent"}`,
                borderRadius:10,
                fontSize: isMobile ? 20 : 18,
                fontFamily:"'Courier New',monospace",
                fontWeight:700, letterSpacing:"0.12em",
                color:"#111827", outline:"none",
                transition:"border-color 0.15s, background 0.15s",
                textTransform:"uppercase",
              }}
              onFocus={() => setFocused(true)}
              onBlur={()  => setFocused(false)}
            />
            {error && (
              <p style={{ fontSize:11, color:"#EF4444", marginTop:6 }}>{error}</p>
            )}
          </div>

          {/* Vehicle type */}
          <div style={{ marginBottom:24 }}>
            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:7, letterSpacing:0.1 }}>
              Vehicle Type
            </label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:6 }}>
              {TYPES.map(({ value, icon:Icon, label }) => {
                const sel = type === value;
                return (
                  <button
                    key={value} type="button"
                    onClick={() => setType(value)}
                    style={{
                      display:"flex", flexDirection:"column", alignItems:"center", gap:6,
                      padding: isMobile ? "14px 4px" : "11px 4px",
                      borderRadius:9, cursor:"pointer",
                      background: sel ? "#EEF2FF" : "#F7F7F7",
                      border:`2px solid ${sel ? "#4F46E5" : "transparent"}`,
                      color: sel ? "#4F46E5" : "#6B7280",
                      transition:"all 0.12s",
                    }}
                  >
                    <Icon size={isMobile ? 18 : 16} />
                    <span style={{ fontSize:9, fontWeight: sel ? 700 : 500, letterSpacing:0.3 }}>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display:"flex", gap:10 }}>
            <button
              type="button" onClick={onClose}
              style={{
                flex:1, height: isMobile ? 50 : 44, borderRadius:9,
                background:"#F7F7F7", border:"none",
                color:"#6B7280", fontSize:13, fontWeight:500, cursor:"pointer",
                transition:"all 0.15s",
              }}
              onMouseOver={e => { e.currentTarget.style.background="#EBEBEB"; e.currentTarget.style.color="#374151"; }}
              onMouseOut={e  => { e.currentTarget.style.background="#F7F7F7"; e.currentTarget.style.color="#6B7280"; }}
            >Cancel</button>

            <button
              type="submit" disabled={loading}
              style={{
                flex:2, height: isMobile ? 50 : 44, borderRadius:9,
                background: loading ? "#A5B4FC" : "#4F46E5",
                border:"none", color:"#fff",
                fontSize:13, fontWeight:700,
                cursor: loading ? "default" : "pointer",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                transition:"background 0.15s",
                boxShadow: loading ? "none" : "0 2px 8px rgba(79,70,229,0.3)",
              }}
              onMouseOver={e => { if (!loading) e.currentTarget.style.background="#4338CA"; }}
              onMouseOut={e  => { if (!loading) e.currentTarget.style.background="#4F46E5"; }}
            >
              {loading && <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} />}
              {loading ? "Fetching FASTag data…" : "Add Vehicle"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>,
    document.body
  );
}
